import { deriveCacheKey } from '../dataSourceCache';
import type { InterpolatedRequest } from '../fetchDataSource';

/**
 * One data source the server fetched for this page, as the browser receives
 * it. `request` fingerprints the request the server sent, so the browser
 * reuses the data only when it would send the same request itself.
 */
export type ServerResolvedSource = {
  readonly status: 'resolved' | 'empty';
  readonly data: unknown;
  readonly request: string;
  /** Epoch milliseconds when the server's fetch completed. */
  readonly resolvedAt: number;
};

/** Every source the server resolved for a page, keyed by source name. */
export type ServerResolvedSources = Readonly<
  Record<string, ServerResolvedSource>
>;

/**
 * Two 32-bit FNV-1a passes with different offsets, as 16 hex characters.
 * Not a security hash: it only tells two request descriptions apart, and it
 * keeps request headers and bodies out of the page.
 */
function fingerprint(text: string): string {
  let a = 0x811c9dc5;
  let b = 0x01000193 ^ 0x5bd1e995;
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    a = Math.imul(a ^ code, 0x01000193) >>> 0;
    b = Math.imul(b ^ code, 0x01000193 + 0x9e3779b1) >>> 0;
  }
  const hex = (value: number) => `00000000${value.toString(16)}`.slice(-8);
  return `${hex(a)}${hex(b)}`;
}

/**
 * The fingerprint of a request, from the same identity the browser's data
 * source cache keys on, so the server and the browser agree exactly when the
 * request on the wire is the same.
 */
export function requestFingerprint(
  request: InterpolatedRequest,
  responsePath: string | undefined,
): string {
  return fingerprint(requestIdentity(request, responsePath));
}

/** The browser's request identity for an already-built request. */
export function requestIdentity(
  request: InterpolatedRequest,
  responsePath: string | undefined,
): string {
  return deriveCacheKey(
    request.method,
    request.endpoint,
    request.query,
    request.body,
    request.headers,
    responsePath,
  );
}

/** The fingerprint of a request identity the browser already derived. */
export function identityFingerprint(identity: string): string {
  return fingerprint(identity);
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads a seed handed to the browser, keeping only well-formed entries.
 * The seed crosses a serialization boundary, so nothing about its shape is
 * trusted.
 */
export function readServerResolvedSources(
  raw: unknown,
): Map<string, ServerResolvedSource> {
  const entries = new Map<string, ServerResolvedSource>();
  if (!isRecord(raw)) return entries;
  for (const name of Object.keys(raw)) {
    const entry = raw[name];
    if (
      isRecord(entry) &&
      (entry.status === 'resolved' || entry.status === 'empty') &&
      typeof entry.request === 'string' &&
      entry.request !== '' &&
      typeof entry.resolvedAt === 'number' &&
      Number.isFinite(entry.resolvedAt)
    ) {
      entries.set(name, {
        status: entry.status,
        data: entry.data,
        request: entry.request,
        resolvedAt: entry.resolvedAt,
      });
    }
  }
  return entries;
}
